import type { ReturnedEntry } from './submitDay';
import type { WorkOrderStatusValue } from './statuses';
import type { WorkOrderVisitStatus } from './visits';
import { round2 } from '../money';

/**
 * 🔒 My Jobs, the technician's board under Work (piece 29; decision 1909;
 * T1199). Every open Work Order with a line or a visit naming the viewer is one
 * card, and the card's column follows the job: the board is read, never
 * dragged. These are the board's rules that need no database: the five
 * columns, which one a card stands in, and the shape the page reads.
 */

export const MY_JOBS_COLUMNS = ['ScheduledToday', 'InProgress', 'Waiting', 'DoneToday', 'ComingUp'] as const;
export type MyJobsColumn = (typeof MY_JOBS_COLUMNS)[number];

/** The string key each column's name reads under; a Title Case name, as a tab label is (decision 1810). */
export const MY_JOBS_COLUMN_KEYS = {
  ScheduledToday: 'myJobs.column.scheduledToday',
  InProgress: 'myJobs.column.inProgress',
  Waiting: 'myJobs.column.waiting',
  DoneToday: 'myJobs.column.doneToday',
  ComingUp: 'myJobs.column.comingUp',
} as const satisfies Record<MyJobsColumn, string>;

/**
 * 🔒 Coming up starts collapsed; every other column starts open (decision 1909).
 * 🔒 Decision 5573 (T2786): read against a board, Coming up opens by itself while Scheduled today is empty.
 */
export function columnStartsCollapsed(column: MyJobsColumn, answer?: Pick<MyJobsAnswer, 'cards'>): boolean {
  if (column !== 'ComingUp') return false;
  return answer ? answer.cards.some((card) => card.column === 'ScheduledToday') : true;
}

/** 🔒 Decision 5573: one booked visit of the person's after today, as Coming up lists it, with the lines that are theirs on it. */
export interface MyJobUpcoming {
  workOrderId: string;
  workOrderNumber: string;
  companyName: string;
  siteName: string;
  visitId: string;
  /** V1, V2 and on. */
  label: string;
  windowStart: string;
  windowEnd: string | null;
  /** The visit's day in the zone the board was read in, YYYY-MM-DD. */
  day: string;
  /** The lines the visit sends the person for, or every line they are on where it names none. */
  lines: { lineId: string; label: string }[];
}

/** 🔒 Decision 5573: Coming up's visits by day, the earliest first, each day's in window order. */
export function comingUpDays(answer: Pick<MyJobsAnswer, 'upcoming'>): { day: string; visits: MyJobUpcoming[] }[] {
  const sorted = [...(answer.upcoming ?? [])].sort((a, b) => (a.windowStart === b.windowStart ? a.workOrderNumber.localeCompare(b.workOrderNumber) : a.windowStart < b.windowStart ? -1 : 1));
  const days: { day: string; visits: MyJobUpcoming[] }[] = [];
  for (const visit of sorted) {
    const last = days[days.length - 1];
    if (last && last.day === visit.day) last.visits.push(visit);
    else days.push({ day: visit.day, visits: [visit] });
  }
  return days;
}

/** 🔒 Decision 5573: the Coming up cards its day list does not already carry, a line due with no booking among them. */
export function comingUpCards(answer: Pick<MyJobsAnswer, 'cards' | 'upcoming'>): MyJobCard[] {
  const listed = new Set((answer.upcoming ?? []).map((visit) => visit.visitId));
  return cardsIn(answer, 'ComingUp').filter((card) => !card.visit || !listed.has(card.visit.id));
}

/** What a card's column is judged from, every fact read for the board's own person. */
export interface MyJobColumnFacts {
  jobStatus: WorkOrderStatusValue;
  /** The job's hold reason; null while the job is not On Hold. */
  holdReason: string | null;
  /** Whether the board's person holds a running clock on this job. */
  clockRunning: boolean;
  /** The state of the person's visit on this job whose window starts today, or null where none does. */
  visitTodayStatus: WorkOrderVisitStatus | null;
  /** Whether the person tapped Leaving on this job today (decision 1902). */
  leftToday: boolean;
}

/**
 * 🔒 The column a card stands in (decision 1909): a running clock reads In
 * progress, a held job Waiting, a finished job or the person's own Leaving
 * today Done today, a started job or visit In progress, a visit booked today
 * Scheduled today, and anything else Coming up. 🔨 The order is the rule: a
 * clock running on a held job still reads In progress, because the person is
 * working it now.
 */
export function myJobColumn(facts: MyJobColumnFacts): MyJobsColumn {
  if (facts.clockRunning) return 'InProgress';
  if (facts.holdReason) return 'Waiting';
  if (facts.jobStatus === 'Complete' || facts.jobStatus === 'Closed') return 'DoneToday';
  if (facts.leftToday) return 'DoneToday';
  if (facts.jobStatus === 'InProgress' || facts.visitTodayStatus === 'InProgress') return 'InProgress';
  if (facts.visitTodayStatus !== null && facts.visitTodayStatus !== 'Complete' && facts.visitTodayStatus !== 'Cancelled') return 'ScheduledToday';
  return 'ComingUp';
}

/** The person's own visit on the card's job: the one booked today, else the next one still to come. */
export interface MyJobVisit {
  id: string;
  /** V1, V2 and on. */
  label: string;
  windowStart: string;
  windowEnd: string | null;
  status: WorkOrderVisitStatus;
  /** Whether the window starts today in the board's zone. */
  today: boolean;
  isLead: boolean;
  /** Whether the board's person is on the visit; the taps are offered only to a worker on it (decision 1982). */
  onVisit: boolean;
  enRouteAt: string | null;
  startedAt: string | null;
  /** 🔒 T1166: the drive time in minutes from the person's dispatch origin to the site, which On My Way pre-fills; null where none is known. */
  driveMinutes: number | null;
}

/** One labour line the board's person is on; 🔨 hours only, never a rate (decision 1909). */
export interface MyJobLine {
  lineId: string;
  /** The line's number and its description's first line, as the Labour tile reads it. */
  label: string;
  /** The person's own hours on the line today, their running clock aside. */
  hoursToday: number;
  /** The person's running clock on this line, where one runs. */
  running: { entryId: string; startedAt: string } | null;
}

export interface MyJobCard {
  workOrderId: string;
  workOrderNumber: string;
  status: WorkOrderStatusValue;
  companyName: string;
  siteName: string;
  /** Whether the job's site runs gate sign-in, where the gate is the clock (decision 1904). */
  gated: boolean;
  holdReason: string | null;
  column: MyJobsColumn;
  visit: MyJobVisit | null;
  lines: MyJobLine[];
  /** The person's Travel entries on the job today, in hours. */
  travelHoursToday: number;
  /** 🔒 Piece 29 (T389): the job's checklist items closed and in all; absent or null where the job carries none. */
  checklist?: { closed: number; total: number } | null;
  /** 🔒 Decision 2594 (T1175): the job's billable work has passed the customer PO's amount; the card says so with no figure, as My Jobs carries no money. Absent from an older reply. */
  poPassed?: boolean;
  /** 🔒 Decision 3072 (T2183): put on the viewer's board since they last opened My Jobs, marked New; absent on another person's board. */
  isNew?: boolean;
}

/**
 * 🔒 What My Jobs remembers of its last opening, per person (decision 3072): the
 * open jobs the board showed then, on the person's own row of the one
 * preference store. A job on the board now that was not on it then is new.
 */
export interface MyJobsSeen {
  workOrderIds: string[];
  openedAt: string;
}

/** The stored row's layout read as what My Jobs remembers; anything else reads as never opened. */
export function readMyJobsSeen(layout: unknown): MyJobsSeen | null {
  if (!layout || typeof layout !== 'object') return null;
  const row = layout as { workOrderIds?: unknown; openedAt?: unknown };
  if (!Array.isArray(row.workOrderIds) || typeof row.openedAt !== 'string') return null;
  return { workOrderIds: row.workOrderIds.filter((id): id is string => typeof id === 'string'), openedAt: row.openedAt };
}

/** 🔒 Decision 3072: the jobs on the board that were not on it at the last opening; a person who never opened My Jobs has every job new. */
export function newSinceSeen(jobIds: readonly string[], seen: MyJobsSeen | null): string[] {
  if (seen === null) return [...jobIds];
  const had = new Set(seen.workOrderIds);
  return jobIds.filter((id) => !had.has(id));
}

/** One person whose board a view-all viewer may read (decision 1909). */
export interface MyJobsPerson {
  employmentId: string;
  name: string;
}

/** What My Jobs reads (decision 1909). */
export interface MyJobsAnswer {
  /** The employment the board is read for; null where the viewer holds none and named nobody. */
  employmentId: string | null;
  personName: string | null;
  /** 🔨 Whether the board is the viewer's own: a board read through the Person filter offers no control, because nobody clocks for anybody else (decision 0127). */
  own: boolean;
  /** The people a view-all viewer may pick; null for a viewer reading their own board alone. */
  people: MyJobsPerson[] | null;
  /** Today in the zone the board was read in, YYYY-MM-DD. */
  today: string;
  /** The person's one running clock, on whichever job it runs (decision 1904). */
  running: { entryId: string; workOrderId: string; lineId: string | null; startedAt: string } | null;
  /** Whether the viewer holds `workorder.time.typed`, which Record Time asks (decision 1904); absent reads as no. */
  canRecord?: boolean;
  /** 🔒 The person's own entries an approver returned in the last fortnight, each with its reason (decision 1983); empty on another person's board. */
  returned?: ReturnedEntry[];
  cards: MyJobCard[];
  /** 🔒 Decision 5573: every booked visit of the person's after today, which Coming up lists by day; absent from an older reply. */
  upcoming?: MyJobUpcoming[];
}

/** The one control a line's row offers on the viewer's own board (piece 29's My Jobs passage; decision 1981). */
export type MyJobLineControl = 'ClockOut' | 'ClockIn' | 'RecordTime';

/**
 * 🔒 Each labour line is one row with one control: Clock Out where its clock
 * runs, Record Time at a gated site where the gate is the clock, and Clock In
 * everywhere else on a job still open. 🔨 A board read for somebody else offers none, and Record
 * Time is offered only to a role holding its capability.
 */
export function myJobLineControl(input: { own: boolean; running: boolean; gated: boolean; canRecord: boolean; jobStatus?: WorkOrderStatusValue }): MyJobLineControl | null {
  if (!input.own) return null;
  if (input.running) return 'ClockOut';
  if (input.gated) return input.canRecord ? 'RecordTime' : null;
  // 🔨 A Complete, Closed or Void job takes no more clocked time (decision 1904), so its card offers no Clock In the server would refuse.
  if (input.jobStatus === 'Complete' || input.jobStatus === 'Closed' || input.jobStatus === 'Void') return null;
  return 'ClockIn';
}

/** The cards of one column, in the order the board draws them: a visit's window first, then the job number. */
export function cardsIn(answer: Pick<MyJobsAnswer, 'cards'>, column: MyJobsColumn): MyJobCard[] {
  const at = (card: MyJobCard) => card.visit?.windowStart ?? '9999';
  return answer.cards.filter((card) => card.column === column).sort((a, b) => (at(a) === at(b) ? a.workOrderNumber.localeCompare(b.workOrderNumber) : at(a) < at(b) ? -1 : 1));
}

/** Hours as the board writes them: two places at most, and never a rate beside them. */
export function hoursLabel(hours: number): string {
  return `${round2(hours)} h`;
}

/** A job's state as a card reads it, two words where the stored value runs them together. */
export function jobStateLabel(status: WorkOrderStatusValue): string {
  return status === 'InProgress' ? 'In Progress' : status;
}
